import { FileText, ImageIcon, LayoutTemplate, Paperclip, SendHorizontal, X } from 'lucide-react';
import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextArea } from '@/lib';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useSendWhatsappMediaMutation, useSendWhatsappMessageMutation, type WhatsappConversation } from '@/services/endpoints/whatsappInbox';
import {
  ATTACHMENT_ACCEPT,
  CAPTION_MAX_LENGTH,
  TEXT_MAX_LENGTH,
  attachmentMime,
  attachmentProblem,
  contactLabel,
  isWindowOpen,
} from '../inboxUtils';
import { useConversationUiState } from '../useConversationUiState';
import { SendTemplateModal } from './SendTemplateModal';

type Props = { conversation: WhatsappConversation };

const errorMessage = (error: unknown): string | undefined => (error as { data?: { message?: string } } | undefined)?.data?.message;
const isSentNotRecorded = (error: unknown): boolean => errorMessage(error) === 'i18n_WHATSAPP_SENT_NOT_RECORDED';
const failureKey = (error: unknown) => (errorMessage(error) === 'i18n_WHATSAPP_WINDOW_CLOSED' ? 'windowClosedTitle' : 'sendFailed');

const useUnmountedFailureToast = (conversation: WhatsappConversation) => {
  const { t } = useTranslation('whatsappInbox');
  const dispatch = useAppDispatch();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (error: unknown): boolean => {
    if (mounted.current) return false;
    const contact = contactLabel(conversation);
    dispatch(
      showToast(
        isSentNotRecorded(error)
          ? { type: ToastType.ERROR, title: contact, message: t('sentNotRecorded'), autodismiss: false }
          : { type: ToastType.ERROR, title: t('sendFailedFor', { contact }) }
      )
    );
    return true;
  };
};

const AttachmentChip: FC<{ file: File; onRemove: VoidFunction }> = ({ file, onRemove }) => {
  const { t } = useTranslation('whatsappInbox');
  const Icon = file.type.startsWith('image/') ? ImageIcon : FileText;

  return (
    <div className='ml-[42px] flex max-w-full items-center gap-2.5 rounded-[10px] border border-primary-border bg-primary-background p-2 pr-2.5 text-sm text-primary-900'>
      <span className='flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700'>
        <Icon aria-hidden className='size-5' />
      </span>
      <span className='min-w-0 flex-1 truncate font-semibold'>{file.name}</span>
      <button type='button' className='rounded-full p-1.5 text-primary-900/60 hover:bg-white-100' aria-label={t('removeAttachment')} onClick={onRemove}>
        <X className='size-3.5' />
      </button>
    </div>
  );
};

const SessionComposer: FC<Props> = ({ conversation }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const fileInput = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sendMessage, { isLoading: isSendingText }] = useSendWhatsappMessageMutation();
  const [sendMedia, { isLoading: isSendingMedia }] = useSendWhatsappMediaMutation();
  const busy = isSendingText || isSendingMedia;
  const reportIfUnmounted = useUnmountedFailureToast(conversation);
  const args = { workspaceId: workspace.id, conversationId: conversation.id };

  const pick = (picked: File | undefined) => {
    if (!picked) return;
    const issue = attachmentProblem(picked);
    if (issue) {
      setProblem(issue === 'size' ? t('fileTooLarge') : t('fileType'));
      return;
    }
    setProblem(null);
    setFile(picked);
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy || (!file && !text.trim())) return;
    if (file && text.length > CAPTION_MAX_LENGTH) {
      setProblem(t('captionTooLong'));
      return;
    }
    try {
      if (file) {
        await sendMedia({ ...args, file, mime: attachmentMime(file) as string, caption: text.trim() || undefined }).unwrap();
      } else {
        await sendMessage({ ...args, body: { type: 'text', text } }).unwrap();
      }
      setText('');
      setFile(null);
      setProblem(null);
    } catch (error) {
      if (reportIfUnmounted(error)) return;
      if (isSentNotRecorded(error)) {
        setText('');
        setFile(null);
        setProblem(t('sentNotRecorded'));
      } else {
        setProblem(t(failureKey(error)));
      }
    }
  };

  return (
    <form className='relative flex flex-col gap-2 border-t border-primary-border bg-white-100 px-3.5 py-2.5' onSubmit={submit}>
      <input
        ref={fileInput}
        type='file'
        accept={ATTACHMENT_ACCEPT}
        className='hidden'
        data-testid='attachment-input'
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {file ? <AttachmentChip file={file} onRemove={() => setFile(null)} /> : null}
      {problem ? (
        <p role='alert' className='text-sm text-red-600'>
          {problem}
        </p>
      ) : null}
      <div className='flex items-center gap-2.5'>
        <button
          type='button'
          aria-label={t('attach')}
          title={t('attach')}
          className='flex size-8 shrink-0 items-center justify-center rounded-full text-primary-900/60 hover:bg-primary-background hover:text-primary-900'
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip className='size-[18px]' />
        </button>
        <TextArea
          value={text}
          rows={1}
          maxLength={TEXT_MAX_LENGTH}
          wrapperClassName='min-w-0 flex-1'
          className='max-h-40 min-h-10 resize-none rounded-full border px-4 py-2.5 [field-sizing:content]'
          aria-label={file ? t('captionPlaceholder') : t('composerPlaceholder')}
          placeholder={file ? t('captionPlaceholder') : t('composerPlaceholder')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <Button
          type='submit'
          aria-label={t('send')}
          title={t('send')}
          isLoading={busy}
          disabled={!file && !text.trim()}
          className='size-[38px] shrink-0 rounded-full p-0'
        >
          <SendHorizontal className='size-4' />
        </Button>
      </div>
    </form>
  );
};

const ExpiredComposer: FC<Props & { excludeMarketing?: boolean }> = ({ conversation, excludeMarketing = false }) => {
  const { t } = useTranslation('whatsappInbox');
  const [modalOpen, setModalOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const reportIfUnmounted = useUnmountedFailureToast(conversation);

  return (
    <div className='flex flex-wrap items-center gap-3 border-t border-primary-border bg-primary-background px-3.5 py-3'>
      <input
        disabled
        placeholder={excludeMarketing ? t('optOutComposerPlaceholder') : t('expiredComposerPlaceholder')}
        className='min-w-[200px] flex-1 rounded-full border border-primary-border bg-primary-background px-3.5 py-2 text-sm text-primary-900/40'
      />
      <Button type='button' className='inline-flex items-center gap-1.5' onClick={() => setModalOpen(true)}>
        <LayoutTemplate className='size-3.5' />
        {t('sendTemplate')}
      </Button>
      {problem ? (
        <p role='alert' className='w-full text-sm text-red-600'>
          {problem}
        </p>
      ) : null}
      <SendTemplateModal
        conversation={conversation}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSentNotRecorded={setProblem}
        onSendFailed={setProblem}
        onSendSuccess={() => setProblem(null)}
        excludeMarketing={excludeMarketing}
        reportIfUnmounted={reportIfUnmounted}
      />
    </div>
  );
};

const ClosedComposer: FC<Props> = ({ conversation }) => {
  const { t } = useTranslation('whatsappInbox');
  const { reopen } = useConversationUiState(conversation.id);
  return (
    <div className='flex items-center justify-center gap-3.5 border-t border-primary-border bg-primary-background px-3.5 py-3 text-[13px] text-primary-900/60'>
      <span>{t('closedComposerMessage')}</span>
      <Button type='button' intent='secondary' className='!border-brand-600 !text-brand-600' onClick={reopen}>
        {t('reopen')}
      </Button>
    </div>
  );
};

/** Free text inside the 24h window; templates via modal when expired; local closed/opt-out gates. */
export const Composer: FC<Props> = ({ conversation }) => {
  const { isClosed, optedOut } = useConversationUiState(conversation.id);

  if (isClosed) {
    return <ClosedComposer key={conversation.id} conversation={conversation} />;
  }

  if (isWindowOpen(conversation.windowOpenUntil)) {
    return <SessionComposer key={conversation.id} conversation={conversation} />;
  }

  return <ExpiredComposer key={conversation.id} conversation={conversation} excludeMarketing={optedOut} />;
};
